/**
 * The filtering behind the Select's `searchable` menu (PD-15027).
 *
 * A long Select (a customer's forty locations on the storefront) is faster to
 * narrow by typing than to scroll. This keeps the matching rules in one pure
 * function so the component only renders what it returns:
 *
 * - The query is split on whitespace and every word must appear, ignoring
 *   case, somewhere in the option's label or subtitle. "detroit 104" finds
 *   "Detroit Main St" whose subtitle is customer number "UT-10446".
 * - Group headings survive filtering. A heading is drawn above the first
 *   VISIBLE option of each run of options sharing a group, and a group with
 *   no matching option loses its heading with it. With an empty query the
 *   result is every option with exactly the headings the unfiltered menu
 *   draws (a heading wherever the group differs from the option before).
 * - Disabled options still match, so the user sees why their search found
 *   nothing selectable rather than an empty list.
 */
export interface SearchableSelectOptionModel {
	label: string;
	subtitle?: string;
	group?: string;
	disabled?: boolean;
}

export interface SelectSearchRowModel<T extends SearchableSelectOptionModel> {
	/** The option's position in the caller's list. */
	index: number;
	option: T;
	/**
	 * The group heading to draw immediately above this option, or null when
	 * it continues the previous visible option's group or has no group.
	 */
	heading: string | null;
}

/** Split a typed query into the lowercase words that must all match. */
export function selectSearchTerms(query: string | null | undefined): Array<string> {
	return (query ?? '')
		.trim()
		.toLocaleLowerCase()
		.split(/\s+/)
		.filter((term) => term !== '');
}

/** True when every term appears in the option's label or subtitle. */
export function selectOptionMatchesTerms(
	option: SearchableSelectOptionModel,
	terms: ReadonlyArray<string>,
): boolean {
	if (terms.length === 0) {
		return true;
	}
	const haystack =
		`${option.label} ${option.subtitle ?? ''}`.toLocaleLowerCase();
	return terms.every((term) => haystack.includes(term));
}

/**
 * The rows the searchable menu shows for `query`, in the caller's order, each
 * carrying the group heading to draw above it.
 */
export function filterSelectOptions<T extends SearchableSelectOptionModel>(
	options: ReadonlyArray<T> | null | undefined,
	query: string | null | undefined,
): Array<SelectSearchRowModel<T>> {
	if (options == null) {
		return [];
	}

	const terms = selectSearchTerms(query);
	const rows: Array<SelectSearchRowModel<T>> = [];
	// The group of the previous VISIBLE option. Undefined before the first
	// one, which is what the unfiltered menu compares the first option with.
	let previousGroup: string | undefined;

	options.forEach((option, index) => {
		if (!selectOptionMatchesTerms(option, terms)) {
			return;
		}
		const group = option.group;
		const heading =
			group != null && group !== '' && group !== previousGroup ? group : null;
		rows.push({ index, option, heading });
		previousGroup = group;
	});

	return rows;
}
